import {
  documentPage,
  logoInput,
  type MeDto,
  renderOfferTemplate,
  renderProtocolTemplate,
  renderTemplate,
  SAMPLE_FIELDS,
  SAMPLE_OFFER_SNAPSHOT,
  SAMPLE_PROTOCOL,
  TEMPLATE_KINDS,
  TEMPLATE_LOGO_KEY,
  type TemplateDto,
  type TemplateKind,
} from '@bqcrm/shared';
import { Alert, Badge, Button, Card, FileButton, Grid, Group, Modal, NavLink, Select, Stack, Switch, Text, TextInput, Title } from '@mantine/core';
import { IconFileImport, IconPlus } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../api';
import { can } from '../auth';
import { PageHeader } from '../components/PageHeader';
import { QueryState } from '../components/QueryState';
import { EmptyState, SectionCard } from '../components/ui';
import { useCompanyLogo, useInlinedImages } from '../components/docImages';
import { DocumentPreview, RichEditor } from '../components/RichEditor';
import { useCompanies } from '../data';
import { formatDateTime } from '../format';
import { notifyError, notifyOk } from '../notify';
import { useSaveShortcut } from '../hooks';

const SAMPLE_BLOCKS = {
  zalaczniki:
    '<ol><li>Załącznik nr 1 — oferta nr OF/B/2026/0001, wariant „Pompa ciepła 10 kW — komplet”, kwota 45 964,80 zł brutto, zaakceptowana 24 października 2026 (akceptacja online, Jan Kowalski). Załącznik stanowi integralną część umowy.</li></ol>',
  tabela_pozycji:
    '<table class="dok-tabela"><thead><tr><th>Lp.</th><th>Przedmiot</th><th class="n">Ilość</th><th class="n">Cena netto</th><th class="n">VAT</th><th class="n">Wartość netto</th></tr></thead><tbody><tr><td>1</td><td>Pompa ciepła 10 kW — komplet</td><td class="n">1 kpl.</td><td class="n">34 900,00</td><td class="n">8%</td><td class="n">34 900,00</td></tr></tbody></table>',
  harmonogram_platnosci:
    '<table class="dok-tabela"><thead><tr><th>Lp.</th><th>Płatność</th><th class="n">Kwota brutto</th><th>Termin</th></tr></thead><tbody><tr><td>1</td><td>Zaliczka 30%</td><td class="n">13 789,44 zł</td><td>31 października 2026</td></tr><tr><td>2</td><td>Płatność końcowa po odbiorze</td><td class="n">32 175,36 zł</td><td>po odbiorze prac</td></tr></tbody></table>',
};

/**
 * Podgląd na danych przykładowych — ten sam kod co PDF na serwerze (pakiet shared). Grafiki i logo jako obrazy
 * w treści (body już z obrazami — useInlinedImages; logoSrc — logo firmy szablonu albo pierwszej firmy z logo).
 */
function renderSample(kind: TemplateKind, body: string, logoSrc: string | null): { html: string; missing: string[] } {
  if (kind === 'oferta') {
    const r = renderOfferTemplate(body, SAMPLE_OFFER_SNAPSHOT, { logoSrc });
    return { html: documentPage(r.html, { title: 'Podgląd oferty', color: SAMPLE_OFFER_SNAPSHOT.company.color, draft: false, font: 'sans' }), missing: r.missing };
  }
  if (kind === 'protokol') {
    const r = renderProtocolTemplate(body, { ...SAMPLE_PROTOCOL, logoSrc });
    return { html: documentPage(r.html, { title: 'Podgląd protokołu', color: '#0b7d62', draft: false }), missing: r.missing };
  }
  const logo = logoInput(logoSrc);
  const r = renderTemplate(body, { fields: { ...SAMPLE_FIELDS, ...logo.field }, blocks: { ...SAMPLE_BLOCKS, ...logo.block } });
  return { html: documentPage(r.html, { title: 'Podgląd umowy', color: '#0b7d62', draft: false }), missing: r.missing };
}

const KIND_INFO: Record<TemplateKind, { uses: string; missing: string }> = {
  umowa: { uses: 'nowe umowy biorą najnowszą wersję szablonu domyślnego', missing: 'umowy z takim szablonem nie da się wydać' },
  aneks: { uses: 'nowe aneksy biorą najnowszą wersję szablonu domyślnego', missing: 'aneksu z takim szablonem nie da się wydać' },
  oferta: {
    uses: 'oferta zapamiętuje wersję szablonu domyślnego w chwili wydania — z niej powstaje PDF; podgląd projektu oferty korzysta z bieżącej wersji',
    missing: 'oferty z takim szablonem nie da się wydać',
  },
  protokol: {
    uses: 'protokół zapamiętuje wersję szablonu domyślnego w chwili przyjęcia z telefonu — z niej powstaje PDF. Jeden szablon obsługuje protokoły odbioru, uruchomienia, przeglądu i serwisu (tytuł wstawia pole „Tytuł”); podpis i zdjęcia są dodawane zawsze',
    missing: 'protokoły dostaną układ wbudowany zamiast tego szablonu',
  },
};


function TemplateEditor({ t, editable }: { t: TemplateDto; editable: boolean }) {
  const companies = useCompanies();
  const qc = useQueryClient();
  const [body, setBody] = useState(t.latest.bodyHtml);
  const [note, setNote] = useState('');
  const [settings, setSettings] = useState({ name: t.name, companyId: t.companyId, active: t.active, isDefault: t.isDefault });
  const [preview, setPreview] = useState(false);
  const [importWarnings, setImportWarnings] = useState<string[]>([]);
  useEffect(() => {
    setBody(t.latest.bodyHtml);
    setSettings({ name: t.name, companyId: t.companyId, active: t.active, isDefault: t.isDefault });
  }, [t]);
  const dirty = body !== t.latest.bodyHtml;
  useSaveShortcut(editable && dirty, () => saveVersion.mutate());

  const saveVersion = useMutation({
    mutationFn: () => api<TemplateDto>(`/templates/${t.id}/versions`, { method: 'POST', body: { bodyHtml: body, note } }),
    onSuccess: () => {
      notifyOk('Zapisano nową wersję szablonu');
      setNote('');
      void qc.invalidateQueries({ queryKey: ['templates'] });
    },
    onError: notifyError,
  });
  const saveSettings = useMutation({
    mutationFn: () => api<TemplateDto>(`/templates/${t.id}`, { method: 'PUT', body: settings }),
    onSuccess: () => {
      notifyOk('Zapisano ustawienia szablonu');
      void qc.invalidateQueries({ queryKey: ['templates'] });
    },
    onError: notifyError,
  });
  const importDocx = async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    const res = await fetch('/api/templates/import-docx', { method: 'POST', body: form, credentials: 'same-origin' });
    const data = (await res.json()) as { bodyHtml?: string; warnings?: string[]; message?: string };
    if (!res.ok) return notifyError(new Error(data.message ?? 'Nie udało się zaimportować pliku'));
    setBody(data.bodyHtml ?? '');
    setImportWarnings(data.warnings ?? []);
    notifyOk('Zaimportowano treść z Worda — sprawdź formatowanie i wstaw pola danych');
  };
  const logoCompany = (companies.data ?? []).find((c) => c.id === settings.companyId) ?? (companies.data ?? []).find((c) => c.logoFileId);
  const logoSrc = useCompanyLogo(logoCompany?.id, logoCompany?.logoFileId);
  const rendered = renderSample(t.kind, useInlinedImages(body), logoSrc);
  const usesLogo = body.includes(`{{${TEMPLATE_LOGO_KEY}}}`);

  return (
    <Stack>
      <Card withBorder>
        <Group align="flex-end" wrap="wrap">
          <TextInput label="Nazwa" value={settings.name} disabled={!editable} onChange={(e) => setSettings({ ...settings, name: e.currentTarget.value })} w={{ base: '100%', sm: 340 }} />
          <Select
            label="Firma"
            data={[{ value: '', label: 'Wspólny dla wszystkich firm' }, ...(companies.data ?? []).map((c) => ({ value: c.id, label: c.shortName }))]}
            value={settings.companyId ?? ''}
            disabled={!editable}
            onChange={(v) => setSettings({ ...settings, companyId: v || null })}
            allowDeselect={false}
            w={{ base: '100%', sm: 240 }}
          />
          <Switch label="Domyślny" checked={settings.isDefault} disabled={!editable} onChange={(e) => setSettings({ ...settings, isDefault: e.currentTarget.checked })} />
          <Switch label="Aktywny" checked={settings.active} disabled={!editable} onChange={(e) => setSettings({ ...settings, active: e.currentTarget.checked })} />
          {editable && (
            <Button variant="light" onClick={() => saveSettings.mutate()} loading={saveSettings.isPending}>
              Zapisz ustawienia
            </Button>
          )}
        </Group>
      </Card>
      <Alert variant="light">
        Zapis treści tworzy nową wersję szablonu (obecnie v{t.latest.versionNo}). Dokumenty już wygenerowane się nie zmieniają; {KIND_INFO[t.kind].uses}.
        {t.usedByDocuments > 0 && ` Szablon użyto w ${t.usedByDocuments} dokumentach.`}
      </Alert>
      {importWarnings.length > 0 && (
        <Alert color="yellow" title="Uwagi z importu Worda">
          {importWarnings.slice(0, 5).join(' · ')}
        </Alert>
      )}
      <RichEditor value={body} onChange={setBody} editable={editable} kind={t.kind} imageUpload={editable} />
      {usesLogo && !logoCompany?.logoFileId && (
        <Alert color="yellow" title="Brak logo firmy">
          Szablon wstawia logo firmy, a {settings.companyId ? 'ta firma' : 'żadna firma'} nie ma jeszcze logo — w dokumencie w tym miejscu nic się nie pojawi. Logo wgrywa się w Ustawieniach → Firmy.
        </Alert>
      )}
      {rendered.missing.length > 0 && (
        <Alert color="red" title="Nieznane pola">
          {rendered.missing.join(', ')} — {KIND_INFO[t.kind].missing}.
        </Alert>
      )}
      <Group justify="space-between" wrap="wrap">
        <Group wrap="wrap">
          <Button variant="default" onClick={() => setPreview(true)}>
            Podgląd na danych przykładowych
          </Button>
          {editable && (
            <FileButton onChange={(f) => f && void importDocx(f)} accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document">
              {(props) => (
                <Button {...props} variant="default" leftSection={<IconFileImport size={16} />}>
                  Importuj z Worda (.docx)
                </Button>
              )}
            </FileButton>
          )}
        </Group>
        {editable && (
          <Group wrap="wrap">
            <TextInput placeholder="Co zmieniono (opcjonalnie)" value={note} onChange={(e) => setNote(e.currentTarget.value)} w={{ base: '100%', sm: 260 }} />
            <Button onClick={() => saveVersion.mutate()} loading={saveVersion.isPending} disabled={!dirty}>
              Zapisz jako nową wersję
            </Button>
          </Group>
        )}
      </Group>
      <SectionCard title="Historia wersji">
        {t.versions.map((v) => (
          <Text key={v.id} size="sm">
            v{v.versionNo} · {formatDateTime(v.createdAt)} · {v.createdByName ?? 'system'} {v.note && `· ${v.note}`}
          </Text>
        ))}
      </SectionCard>
      <Modal opened={preview} onClose={() => setPreview(false)} title="Podgląd na danych przykładowych" size="xl">
        <DocumentPreview html={rendered.html} />
      </Modal>
    </Stack>
  );
}

export function TemplatesPage({ me }: { me: MeDto }) {
  const q = useQuery({ queryKey: ['templates'], queryFn: () => api<TemplateDto[]>('/templates') });
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newT, setNewT] = useState<{ kind: TemplateKind; name: string }>({ kind: 'umowa', name: '' });
  const qc = useQueryClient();
  const editable = can(me, 'szablony.edycja');
  const current = q.data?.find((t) => t.id === selected) ?? q.data?.[0];
  // treść startowa: wybrany szablon tego rodzaju, inaczej domyślny tego rodzaju
  const starterBody = (kind: TemplateKind) =>
    (current?.kind === kind ? current : (q.data?.find((t) => t.kind === kind && t.isDefault) ?? q.data?.find((t) => t.kind === kind)))?.latest.bodyHtml ?? '';
  const create = useMutation({
    mutationFn: () => api<TemplateDto>('/templates', { method: 'POST', body: { ...newT, companyId: null, bodyHtml: starterBody(newT.kind) } }),
    onSuccess: (t) => {
      void qc.invalidateQueries({ queryKey: ['templates'] });
      setSelected(t.id);
      setCreating(false);
      notifyOk('Utworzono szablon');
    },
    onError: notifyError,
  });

  return (
    <>
      <PageHeader
        title="Szablony dokumentów"
        description="Wzory umów, aneksów, ofert i protokołów z polami uzupełnianymi danymi systemu. Każdy zapis tworzy nową wersję — wydane dokumenty się nie zmieniają."
        actions={
          editable && (
            <Button leftSection={<IconPlus size={16} />} onClick={() => setCreating(true)}>
              Nowy szablon
            </Button>
          )
        }
      />
      <QueryState isLoading={q.isLoading} error={q.error}>
        <Grid gap="lg">
          <Grid.Col span={{ base: 12, md: 3 }}>
            {(Object.keys(TEMPLATE_KINDS) as TemplateKind[]).map((k) => (
              <div key={k}>
                <Title order={6} c="dimmed" mt="sm" mb={4}>
                  {TEMPLATE_KINDS[k]}
                </Title>
                {q.data
                  ?.filter((t) => t.kind === k)
                  .map((t) => (
                    <NavLink
                      key={t.id}
                      active={current?.id === t.id}
                      label={t.name}
                      description={`v${t.latest.versionNo}${t.active ? '' : ' · nieaktywny'}`}
                      rightSection={t.isDefault ? <Badge size="xs">domyślny</Badge> : undefined}
                      onClick={() => setSelected(t.id)}
                    />
                  ))}
              </div>
            ))}
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 9 }}>
            {current ? (
              <TemplateEditor key={current.id} t={current} editable={editable} />
            ) : (
              <Card padding={0}>
                <EmptyState
                  title="Brak szablonów"
                  hint="Utwórz pierwszy szablon albo zaimportuj treść z Worda."
                  action={
                    editable && (
                      <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setCreating(true)}>
                        Nowy szablon
                      </Button>
                    )
                  }
                />
              </Card>
            )}
          </Grid.Col>
        </Grid>
      </QueryState>
      <Modal opened={creating} onClose={() => setCreating(false)} title="Nowy szablon">
        <Stack>
          <Select
            label="Rodzaj"
            data={Object.entries(TEMPLATE_KINDS).map(([value, label]) => ({ value, label }))}
            value={newT.kind}
            onChange={(v) => v && setNewT({ ...newT, kind: v as TemplateKind })}
            allowDeselect={false}
          />
          <TextInput label="Nazwa" required value={newT.name} onChange={(e) => setNewT({ ...newT, name: e.currentTarget.value })} />
          <Text size="xs" c="dimmed">
            Treść startowa: kopia wybranego (albo domyślnego) szablonu tego rodzaju — możesz ją zastąpić importem z Worda.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setCreating(false)}>
              Anuluj
            </Button>
            <Button onClick={() => create.mutate()} loading={create.isPending} disabled={newT.name.trim().length < 3}>
              Utwórz szablon
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
